import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { SCANNABLE_STAGES, type Engagement, type Scan } from '@ai-secure-twin/shared';
import { api, ApiError } from '../../lib/api';

const isRunning = (scan: Scan) => scan.status === 'QUEUED' || scan.status === 'RUNNING';

const formatInstant = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

/** Why the scan button is off, in the terms of where the engagement actually is. */
function notScannableBecause(stage: Engagement['stage']): string {
  if (stage === 'DRAFT' || stage === 'SCOPE_PENDING_APPROVAL') {
    return 'Scanning becomes available once the scope has been approved.';
  }
  if (stage.endsWith('PENDING_APPROVAL')) {
    return 'Scanning pauses while an approver decides.';
  }
  return 'This engagement is closed; nothing more will be scanned.';
}

/**
 * Starts and reports passive scans.
 *
 * Only `PASSIVE` is offered. Active scanning needs the scope permit *and* a plan approval,
 * and offering a button the server would refuse is worse than not offering it at all.
 */
export function ScanPanel({ engagement }: { engagement: Engagement }) {
  const queryClient = useQueryClient();
  const scopeVersion = engagement.currentScopeVersion;

  const scans = useQuery({
    queryKey: ['scans', engagement.id],
    queryFn: () => api.listScans(engagement.id),
    refetchInterval: (query) => ((query.state.data?.items ?? []).some(isRunning) ? 5000 : false),
  });

  const start = useMutation({
    mutationFn: () => {
      if (scopeVersion === undefined) throw new Error('This engagement has no scope.');
      return api.startScan(engagement.id, { kind: 'PASSIVE', scopeVersion });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['scans', engagement.id] });
      await queryClient.invalidateQueries({ queryKey: ['findings', engagement.id] });
      // Starting the first passive scan moves the engagement into discovery and passive testing.
      await queryClient.invalidateQueries({ queryKey: ['engagement', engagement.id] });
      await queryClient.invalidateQueries({ queryKey: ['engagements'] });
    },
  });

  const items = [...(scans.data?.items ?? [])].sort((a, b) =>
    b.requestedAt.localeCompare(a.requestedAt),
  );
  const active = items.find(isRunning);

  /*
   * Findings are written when a scan finishes, not when it starts. Without this the list — and
   * the progress track counting it — stayed at zero until a reload, while the scan beside it
   * already said "13 findings".
   */
  const wasRunning = useRef(false);
  useEffect(() => {
    const running = Boolean(active);
    if (wasRunning.current && !running) {
      void queryClient.invalidateQueries({ queryKey: ['findings', engagement.id] });
      void queryClient.invalidateQueries({ queryKey: ['engagement', engagement.id] });
    }
    wasRunning.current = running;
  }, [active, engagement.id, queryClient]);

  // The server's own list: past the scope gate, and not waiting at a later one.
  const scannable = SCANNABLE_STAGES.has(engagement.stage);

  return (
    <section className="card panel" aria-labelledby="scan-title">
      <div className="panel-head">
        <h2 id="scan-title" className="panel-title">
          Scans
        </h2>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          disabled={!scannable || start.isPending || Boolean(active) || scopeVersion === undefined}
          onClick={() => start.mutate()}
        >
          {start.isPending ? 'Starting…' : 'Run passive scan'}
        </button>
      </div>

      {!scannable ? (
        <p className="muted" style={{ fontSize: 13 }}>
          {notScannableBecause(engagement.stage)}
        </p>
      ) : null}

      {start.error ? (
        <p className="login-error" role="alert">
          {start.error instanceof ApiError ? start.error.message : 'Could not start the scan.'}
        </p>
      ) : null}

      {scans.isLoading ? (
        <p className="muted">Loading scans…</p>
      ) : items.length === 0 ? (
        <div className="empty">
          <p>No scans yet.</p>
          <p className="muted">
            A passive scan crawls the approved seed URLs and reports what it observes. It sends no
            attack payload — that is active scanning, behind its own gate.
          </p>
        </div>
      ) : (
        <div className="run-list">
          {items.map((scan) => (
            <article key={scan.id} className="run">
              <div className="run-head">
                <span>
                  {scan.kind} · scope v{scan.scopeVersion}
                </span>
                <span
                  className={`status ${isRunning(scan) ? 'status-waiting' : scan.status === 'SUCCEEDED' ? 'status-done' : 'status-active'}`}
                >
                  {scan.status}
                </span>
              </div>
              <p className="meta">
                Requested {formatInstant(scan.requestedAt)}
                {scan.findingCount !== undefined ? ` · ${scan.findingCount} findings` : ''}
              </p>
              {scan.error ? <p className="meta">{scan.error}</p> : null}
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
